import { useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import { usePlaybackStore } from '@/stores';
import { applyLoopWrap, transportTimeAt } from '@/utils/playbackClock';
import { getAudioNow } from '@/utils/audioNow';
import type { ScoreTimeline } from '@/types';

/**
 * Hook for managing playback timing and wait mode logic.
 *
 * OTO-22: position is a pure read of the audio clock through the store's
 * anchor (`transportTimeAt`), never an rAF-accumulated value -- the engine
 * paints on rAF but is not the time authority, so a main-thread stall can
 * delay a frame's paint without ever desyncing the transport itself (see
 * `utils/playbackClock.ts`). The engine never creates, mutates, or clears
 * the anchor; only the store's re-anchor funnel does.
 *
 * Uses refs for all mutable state to avoid stale closure issues
 * in the requestAnimationFrame loop.
 */
export function usePlaybackEngine(timeline: ScoreTimeline | null | undefined) {
  const animationFrameRef = useRef<number | null>(null);
  const lastRafTimestampRef = useRef<number | null>(null);

  // Use refs to avoid stale closures in the animation loop
  const timelineRef = useRef(timeline);
  const stateRef = useRef(usePlaybackStore.getState().state);
  const positionRef = useRef(usePlaybackStore.getState().position);
  const configRef = useRef(usePlaybackStore.getState().config);
  const anchorRef = useRef(usePlaybackStore.getState().anchor);
  // Loop-bounds cache (measure number -> transport time): `find()` over
  // `tl.measures` only needs to rerun when the timeline or the configured
  // loop measure numbers actually change, not on every rAF tick.
  const loopBoundsCacheRef = useRef<{
    timeline: ScoreTimeline;
    loopStart: number;
    loopEnd: number;
    loopStartTime: number;
    loopEndTime: number;
  } | null>(null);

  // Keep refs in sync
  timelineRef.current = timeline;

  useEffect(() => {
    return usePlaybackStore.subscribe((s) => {
      stateRef.current = s.state;
      positionRef.current = s.position;
      configRef.current = s.config;
      anchorRef.current = s.anchor;
    });
  }, []);

  const state = usePlaybackStore((s) => s.state);
  const setPosition = usePlaybackStore((s) => s.setPosition);
  const pause = usePlaybackStore((s) => s.pause);
  const seek = usePlaybackStore((s) => s.seek);
  const wrapToLoopStart = usePlaybackStore((s) => s.wrapToLoopStart);

  // Find the current measure based on time
  const findCurrentMeasure = useCallback((time: number): { measure: number; beat: number } => {
    const tl = timelineRef.current;
    if (!tl) return { measure: 1, beat: 1 };

    for (let i = tl.measures.length - 1; i >= 0; i--) {
      const measure = tl.measures[i];
      if (time >= measure.startTime) {
        const beatDuration = measure.duration / (measure.timeSignature?.numerator || 4);
        const timeInMeasure = time - measure.startTime;
        const beat = Math.floor(timeInMeasure / beatDuration) + 1;
        return { measure: measure.number, beat };
      }
    }

    return { measure: 1, beat: 1 };
  }, []);

  // Main playback loop -- reads everything from refs, no stale closures.
  // Paints only: the audio-clock read below is the sole source of the
  // transport position; this callback never accumulates a delta itself.
  const playbackLoop = useCallback(
    (timestamp: number) => {
      const tl = timelineRef.current;
      if (!tl || stateRef.current !== 'playing') return;

      // Diagnostic only -- does not feed position math. A late/sparse paint
      // frame just means we skipped visual updates; the transport read below
      // is unaffected and never accumulates drift from it (expected on weak
      // devices/background throttling, not an error).
      if (lastRafTimestampRef.current !== null) {
        const gapMs = timestamp - lastRafTimestampRef.current;
        if (gapMs > 250) {
          console.warn(
            `[usePlaybackEngine] rAF gap ${gapMs.toFixed(0)}ms observed at transport time ${positionRef.current.currentTime.toFixed(3)}s`
          );
        }
      }
      lastRafTimestampRef.current = timestamp;

      // A null anchor while playing means the transport is intentionally
      // frozen (tab hidden, T7) -- skip this frame's paint and keep polling
      // so painting resumes the instant it thaws.
      const anchor = anchorRef.current;
      if (!anchor) {
        animationFrameRef.current = requestAnimationFrame(playbackLoop);
        return;
      }

      const newTime = transportTimeAt(anchor, getAudioNow());
      const config = configRef.current;

      // Handle loop
      if (config.loopStart !== null && config.loopEnd !== null) {
        const cache = loopBoundsCacheRef.current;
        let loopStartTime: number;
        let loopEndTime: number;
        if (
          cache &&
          cache.timeline === tl &&
          cache.loopStart === config.loopStart &&
          cache.loopEnd === config.loopEnd
        ) {
          loopStartTime = cache.loopStartTime;
          loopEndTime = cache.loopEndTime;
        } else {
          loopStartTime = tl.measures.find((m) => m.number === config.loopStart)?.startTime || 0;
          loopEndTime =
            tl.measures.find((m) => m.number === config.loopEnd)?.startTime || tl.totalDuration;
          loopBoundsCacheRef.current = {
            timeline: tl,
            loopStart: config.loopStart,
            loopEnd: config.loopEnd,
            loopStartTime,
            loopEndTime,
          };
        }

        const loopWrap = applyLoopWrap(newTime, loopStartTime, loopEndTime);
        if (loopWrap.wrapped) {
          // Pass the pure measure-boundary `loopStartTime` alongside the
          // (possibly overshoot-drifted) `loopWrap.time` landing position --
          // see `loopWrapBoundaryTime` on the store for why the scheduler
          // cursor must reconcile against the former, not the latter.
          wrapToLoopStart(loopWrap.time, loopStartTime);
          animationFrameRef.current = requestAnimationFrame(playbackLoop);
          return;
        }
      }

      // Check for end of score
      if (newTime >= tl.totalDuration) {
        pause();
        return;
      }

      // Update position
      const { measure, beat } = findCurrentMeasure(newTime);

      setPosition({
        currentTime: newTime,
        currentMeasure: measure,
        currentBeat: beat,
      });

      // Continue the loop
      animationFrameRef.current = requestAnimationFrame(playbackLoop);
    },
    [findCurrentMeasure, setPosition, pause, wrapToLoopStart]
  );

  // Start/stop playback based on state
  useEffect(() => {
    const state = usePlaybackStore.getState().state;

    if (state === 'playing') {
      lastRafTimestampRef.current = null;
      animationFrameRef.current = requestAnimationFrame(playbackLoop);
    } else {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [state, playbackLoop]);

  // Reset when timeline changes, via the same re-anchoring path a user seek
  // takes -- not a bare `setPosition` orphan write that the next derived
  // frame would silently overwrite.
  useEffect(() => {
    if (timeline) {
      seek(0);
    }
  }, [timeline, seek]);

  // Background-tab lifecycle (T7, R1): a hidden tab throttles/suspends rAF,
  // which the OTO-22 anchor design already tolerates on its own (a stalled
  // paint never desyncs the audio-clock-derived transport) -- but leaving
  // the anchor running while hidden would let the transport, and any
  // auto-play scheduling built on it, silently keep advancing in the
  // background. Freezing on hide and re-anchoring at the frozen position on
  // show reproduces the OTO-21 100ms-rAF-delta cap's "does not run away"
  // guarantee for a hidden tab (which reliably produces gaps far past that
  // threshold) without reintroducing the delta-cap bug (B3) that removing
  // it fixed. `usePlaybackEngine` is the single owner of this wiring so a
  // transport lifecycle event is never registered from more than one place.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const store = usePlaybackStore.getState();
      if (document.hidden) {
        if (store.state === 'playing') store.freezeTransport();
      } else {
        store.thawTransport();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // AudioContext suspension (T7, R1): browsers can suspend/interrupt the
  // audio context independent of tab visibility (e.g. iOS Safari
  // backgrounding, OS-level audio focus loss). A suspended context can no
  // longer schedule or render sound, so letting transport time keep
  // advancing while `state` stays 'playing' would silently desync audio
  // from the visual cursor with no recovery path -- pausing here mirrors
  // the discontinuity-reconciliation policy (OTO-22 Approach §2b) applied
  // to every other transport-stopping event.
  useEffect(() => {
    const handleContextStateChange = () => {
      const contextState = Tone.context.state as string;
      if (
        (contextState === 'suspended' || contextState === 'interrupted') &&
        usePlaybackStore.getState().state === 'playing'
      ) {
        console.warn(
          `[usePlaybackEngine] AudioContext state=${contextState} while playing -- pausing transport`
        );
        usePlaybackStore.getState().pause();
      }
    };
    Tone.context.on('statechange', handleContextStateChange);
    return () => {
      Tone.context.off('statechange', handleContextStateChange);
    };
  }, []);
}
